import { CSSProperties } from "react";
import classNames, { Argument } from "classnames";
import { BaseProps } from "../BaseProps";
import { getTestId } from "./getTestId";

/**
 * The base property conversion options.
 */
export interface BasePropConversionOptions {
  /**
   * The set of additional component class names.
   */
  readonly className?: Argument;

  /**
   * The test ID prefix.
   */
  readonly testIdPrefix?: string;
}

/**
 * Base HTML properties.
 */
export interface BaseHtmlProps {
  /**
   * The CSS class name.
   */
  readonly className?: string;

  /**
   * The test ID data attribute.
   */
  readonly "data-testid"?: string;

  /**
   * The CSS style.
   */
  readonly style?: CSSProperties;
}

/**
 * The default base prop options.
 */
const DefaultBasePropOptions: BasePropConversionOptions = {};

/**
 * Gets standard React HTML component properties from the specified BaseProps instance.
 * @param baseProps The base properties to get standard properties for.
 * @param options Additional options for generating HTML properties.
 * @returns The standard React HTML component properties.
 */
export function getHtmlPropsFromBaseProps(
  baseProps: BaseProps,
  options: BasePropConversionOptions = DefaultBasePropOptions,
): BaseHtmlProps {
  const { className, testId, style } = baseProps;
  const { className: additionalClassNames, testIdPrefix } = options;

  let dataTestId: string | undefined = undefined;
  if (testId) {
    dataTestId = testIdPrefix !== undefined ? getTestId(testId, testIdPrefix) : getTestId(testId);
  }

  const generatedClassName = classNames(additionalClassNames, className);

  return {
    className: generatedClassName ? generatedClassName : undefined,
    "data-testid": dataTestId,
    style,
  };
}
